/**
 * 默认音乐封面管理模块
 * 自动读取目录中的所有图片文件，不依赖具体文件名
 */

// 使用 Vite 的 import.meta.glob 功能动态导入所有图片
const coverModules = import.meta.glob('../assets/images/music/*.{jpg,jpeg,png,webp}', { 
  eager: true,
  as: 'url'
});

// 过滤掉非图片文件（如 SVG 图标）
const imageExtensions = ['.jpg', '.jpeg', '.png', '.webp'];
const coverUrls = Object.entries(coverModules)
  .filter(([path]) => {
    const extension = path.toLowerCase().substring(path.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  })
  .map(([path, url]) => url)
  .sort(); // 排序以确保一致性

// 默认封面数组（自动包含目录中的所有图片文件）
export const defaultCovers = coverUrls;

console.log(`🎨 [默认封面] 自动加载了 ${defaultCovers.length} 张默认封面图片`);

/**
 * 检查URL是否为默认封面
 * @param {string} coverUrl - 封面URL
 * @returns {boolean} 是否为默认封面
 */
export const isDefaultCover = (coverUrl) => {
  if (!coverUrl) return true;
  
  const defaultCoverPatterns = [
    '/src/assets/images/music/default-music-cover-',
    'default-music-cover-',
    'temp-cover',
    'default-cover',
    'music-cover-' // 兼容旧格式
  ];
  
  return defaultCoverPatterns.some(pattern => 
    coverUrl.includes(pattern)
  ) || coverUrl.endsWith('.jpg') && coverUrl.includes('default');
};

/**
 * 获取随机默认封面
 * @param {string|number} songId - 歌曲ID，用于确保同一首歌始终显示相同的封面
 * @returns {string} 默认封面URL
 */
export const getRandomDefaultCover = (songId) => {
  // 使用歌曲ID的哈希值来确保确定性随机
  const hash = songId.toString().split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  
  const index = Math.abs(hash) % defaultCovers.length;
  return defaultCovers[index];
};

/**
 * 将文件系统路径转换为HTTP URL
 * @param {string} coverUrl - 原始封面URL
 * @returns {string|null} 转换后的HTTP URL或null
 */
const convertToHttpCoverUrl = (coverUrl) => {
  if (!coverUrl || typeof coverUrl !== 'string') {
    return null;
  }
  
  const trimmedUrl = coverUrl.trim();
  if (trimmedUrl === '') {
    return null;
  }
  
  // 如果已经是HTTP路径，直接返回
  if (trimmedUrl.startsWith('/uploads/music/music-cover/') || 
      trimmedUrl.startsWith('http://') || 
      trimmedUrl.startsWith('https://') ||
      trimmedUrl.startsWith('data:')) {
    return trimmedUrl;
  }
  
  // 如果是文件系统路径，转换为HTTP路径
  if (trimmedUrl.includes('/srv/uploads/music/music-cover/') || 
      trimmedUrl.includes('/srv/music-uploads/covers/')) {
    const filename = trimmedUrl.split('/').pop();
    if (!filename) {
      console.warn('🔄 [封面URL转换] 无法提取文件名:', trimmedUrl);
      return null;
    }
    
    // 统一转换为 /uploads/music/music-cover/ 路径
    const httpUrl = `/uploads/music/music-cover/${filename}`;
    
    console.log(`🔄 [封面URL转换] ${trimmedUrl} -> ${httpUrl}`);
    return httpUrl;
  }
  
  // 其他情况（如默认封面路径）直接返回
  return trimmedUrl;
};

/**
 * 获取歌曲封面URL
 * @param {Object} song - 歌曲对象
 * @returns {string} 封面URL
 */
export const getSongCoverUrl = (song) => {
  if (!song) return defaultCovers[0];
  
  // 如果是默认封面，返回随机分配的封面
  if (isDefaultCover(song.coverUrl)) {
    return getRandomDefaultCover(song.id);
  }
  
  // 转换文件系统路径为HTTP路径
  const convertedUrl = convertToHttpCoverUrl(song.coverUrl);
  
  // 如果转换成功，返回转换后的URL；否则返回默认封面
  return convertedUrl || getRandomDefaultCover(song.id);
};

/**
 * 配置选项
 */
export const coverConfig = {
  // 默认封面存放路径
  basePath: '../assets/images/music/',
  // 支持的图片格式
  supportedExtensions: ['.jpg', '.jpeg', '.png', '.webp'],
  // 默认封面总数（动态计算）
  get count() {
    return defaultCovers.length;
  },
  // 自动检测模式
  autoDetect: true
};

/**
 * 获取所有可用的默认封面信息
 * @returns {Array} 封面信息数组
 */
export const getCoverInfo = () => {
  return defaultCovers.map((url, index) => ({
    index,
    url,
    filename: url.substring(url.lastIndexOf('/') + 1)
  }));
};

export default {
  defaultCovers,
  isDefaultCover,
  getRandomDefaultCover,
  getSongCoverUrl,
  coverConfig,
  getCoverInfo
};
